/**
 * Sale status constants and utilities for the application
 */

// Sale status enum
export const SALE_STATUS = {
  PAID: 'PAID',
  OFFICIAL_PAID: 'OFFICIAL_PAID',
  SELLER_SHIPPED: 'SELLER_SHIPPED',
  WAREHOUSE_RECEIVED: 'WAREHOUSE_RECEIVED',
  VERIFYING: 'VERIFYING',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED'
};

// cancellation reason enum
export const CANCELLATION_REASON = {
  SELLER_TIMEOUT: 'SELLER_TIMEOUT',
  VERIFICATION_FAILED: 'VERIFICATION_FAILED',
  ADMIN_CANCELLED: 'ADMIN_CANCELLED',
};

// Status to step mapping for the stepper component
export const getSaleStatusStepIndex = (status, isOfficial) => {
  const statusToStepMap = isOfficial ? {
    [SALE_STATUS.PAID]: 0,
    [SALE_STATUS.COMPLETED]: 1
  } : {
    [SALE_STATUS.PAID]: 0,
    [SALE_STATUS.SELLER_SHIPPED]: 1,
    [SALE_STATUS.WAREHOUSE_RECEIVED]: 2,
    [SALE_STATUS.VERIFYING]: 3,
    [SALE_STATUS.COMPLETED]: 4
  };
  
  return statusToStepMap[status] || 0;
};

// Status to stepper label mapping
export const getSaleStepperLabel = (status, isOfficial) => {
  if (status != SALE_STATUS.CANCELLED && isOfficial && getSaleStatusStepIndex(status, isOfficial) === 0) {
    return 'sales.labels.paid';
  } else {
    const stepperLabels = {
      [SALE_STATUS.PAID]: 'sales.labels.paid',
      [SALE_STATUS.SELLER_SHIPPED]: 'sales.labels.seller_shipped',
      [SALE_STATUS.WAREHOUSE_RECEIVED]: 'sales.labels.warehouse_received',
      [SALE_STATUS.VERIFYING]: 'sales.labels.verifying',
      [SALE_STATUS.COMPLETED]: 'sales.labels.completed',
      [SALE_STATUS.CANCELLED]: 'sales.labels.cancelled'
    };
    return stepperLabels[status] || '';
 }
};

// Status to message title mapping
export const getSaleMessageTitle = (status, timeLeft) => {
  if (status === SALE_STATUS.PAID && timeLeft) {
    return 'sales.messages.shipping_countdown';
  }
  
  const messageTitles = {
    [SALE_STATUS.PAID]: 'sales.status.paid',
    [SALE_STATUS.SELLER_SHIPPED]: 'sales.status.seller_shipped',
    [SALE_STATUS.WAREHOUSE_RECEIVED]: 'sales.status.warehouse_received',
    [SALE_STATUS.VERIFYING]: 'sales.status.verifying',
    [SALE_STATUS.COMPLETED]: 'sales.status.completed',
    [SALE_STATUS.CANCELLED]: 'sales.status.canceled'
  };

  return messageTitles[status] || '';
};

// Status to translation key mapping
export const getSaleStatusTranslationKey = (status, isOfficial) => {
  if (status != SALE_STATUS.CANCELLED && isOfficial && getSaleStatusStepIndex(status, isOfficial) === 0) {
    return `sales.status.${SALE_STATUS.OFFICIAL_PAID.toLowerCase()}`;
  } else {
    return `sales.status.${status.toLowerCase()}`;
  }
};

// Helper to get sale message title translation key
export const getSaleMessageTitleKey = (status, isOfficial) => {
  if (status != SALE_STATUS.CANCELLED && isOfficial && getSaleStatusStepIndex(status, isOfficial) === 0) {
    return `sales.messageTitles.${SALE_STATUS.OFFICIAL_PAID.toLowerCase()}`;
  } else {
    return `sales.messageTitles.${status.toLowerCase()}`;
  }
};

// Helper to get sale explanation translation key
export const getSaleExplanationKey = (status, isOfficial) => {
  if (status != SALE_STATUS.CANCELLED && isOfficial && getSaleStatusStepIndex(status, isOfficial) === 0) {
    return `sales.explanations.${SALE_STATUS.OFFICIAL_PAID.toLowerCase()}`;
  } else {
    return `sales.explanations.${status.toLowerCase()}`;
  }
};

// Helper to get explanation translation key
export const getCancellationReasonKey = (cancellationReason, verificationNote) => {
  switch (cancellationReason) {
    case CANCELLATION_REASON.SELLER_TIMEOUT:
    case CANCELLATION_REASON.ADMIN_CANCELLED:
      return `sales.explanations.cancelled.${cancellationReason.toLowerCase()}`;
    case CANCELLATION_REASON.VERIFICATION_FAILED:
      return verificationNote ? `sales.explanations.cancelled.${cancellationReason.toLowerCase()}` : 'sales.explanations.cancelled.default';
    default:
      return 'sales.explanations.cancelled.default';
  }
};

// Helper to check if a sale status is cancelled
export const isSaleCancelled = (status) => {
  return status === SALE_STATUS.CANCELLED;
};